import axios from "axios";
import {
  ApiResponse,
  MetadataFile,
  DataFile,
  Job,
  JobStatus,
  CreateJobRequest,
  CreateJobResponse,
  ValidationDetails,
  ProfileReport,
  ProfileReportSummary,
  ProfileReportListResponse,
  DataFileRowsResponse,
  Project,
  ProjectCreate,
  ProjectUpdate
} from "../types";

// API Configuration
const API_BASE_URL = "https://141-148-93-219.sslip.io/backend";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("API Response Error:", error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API Service Class
export class ApiService {
  // Resolve the active project id (configured elsewhere in the app)
  private static getCurrentProjectId(): number | undefined {
    try {
      const stored = localStorage.getItem('currentProjectId') || localStorage.getItem('projectId');
      if (!stored) return undefined;
      const n = Number(stored);
      return Number.isFinite(n) ? n : undefined;
    } catch {
      return undefined;
    }
  }
  // Set current project id centrally so all API calls can include project_id
  static setCurrentProjectId(projectId: number | undefined) {
    try {
      if (projectId === undefined || projectId === null) {
        localStorage.removeItem('currentProjectId');
      } else {
        localStorage.setItem('currentProjectId', String(projectId));
      }
    } catch {
      // ignore storage errors
    }
  }
  // Metadata Files
  static async getMetadataFiles(
    skip: number = 0,
    limit: number = 100,
    projectId?: number
  ): Promise<ApiResponse<MetadataFile>> {
    try {
      const params = new URLSearchParams();
      params.append('skip', String(skip));
      params.append('limit', String(limit));
      const pid = projectId ?? ApiService.getCurrentProjectId();
      if (pid !== undefined) params.append('project_id', String(pid));

      const response = await apiClient.get<ApiResponse<MetadataFile>>(
        `/v1/dap/files/metadata?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching metadata files:", error);
      throw new Error("Failed to fetch metadata files");
    }
  }

  static async getMetadataFileById(id: number): Promise<MetadataFile> {
    try {
      const response = await apiClient.get<MetadataFile>(
        `/v1/dap/files/metadata/${id}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching metadata file ${id}:`, error);
      throw new Error(`Failed to fetch metadata file with ID ${id}`);
    }
  }

  // Data Files
  static async getDataFiles(
    skip: number = 0,
    limit: number = 100,
    projectId?: number
  ): Promise<ApiResponse<DataFile>> {
    try {
      const params = new URLSearchParams();
      params.append('skip', String(skip));
      params.append('limit', String(limit));
      const pid = projectId ?? ApiService.getCurrentProjectId();
      if (pid !== undefined) params.append('project_id', String(pid));

      const response = await apiClient.get<ApiResponse<DataFile>>(
        `/v1/dap/files/data?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching data files:", error);
      throw new Error("Failed to fetch data files");
    }
  }

  static async getDataFileById(id: number): Promise<DataFile> {
    try {
      const response = await apiClient.get<DataFile>(
        `/v1/dap/files/data/${id}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching data file ${id}:`, error);
      throw new Error(`Failed to fetch data file with ID ${id}`);
    }
  }

  static async uploadDataFile(formData: FormData): Promise<{data_file_id: number, file_name: string, file_path: string, created_by: string, message: string}> {
    try {
      // Add created_by parameter to the form data
      formData.append('created_by', 'user');
      // Attach project_id if available (no hardcoding)
      const pid = ApiService.getCurrentProjectId();
      if (pid !== undefined) {
        formData.append('project_id', String(pid));
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/v1/dap/files/upload/data`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000, // 30 seconds for file upload
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading data file:", error);
      throw new Error("Failed to upload data file");
    }
  }

  // Get data file rows with pagination support
  static async getDataFileRows(
    fileId: number,
    page: number = 1,
    limit: number = 50,
    includeCounts: boolean = false,
    filter?: any
  ): Promise<DataFileRowsResponse> {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      params.append("include_counts", includeCounts.toString());
      if (filter) params.append("filter", JSON.stringify(filter));

      const response = await apiClient.get<DataFileRowsResponse>(
        `/v1/dap/files/data/${fileId}/rows?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching data file rows for file ${fileId}:`, error);
      throw new Error(`Failed to fetch data file rows for file with ID ${fileId}`);
    }
  }

  // Update data file rows
  static async updateDataFileRows(
    fileId: number,
    rows: Record<string, any>[]
  ): Promise<any> {
    try {
      const response = await apiClient.put(
        `/v1/dap/files/data/${fileId}/rows`,
        { rows }
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating data file rows for file ${fileId}:`, error);
      throw new Error(`Failed to update data file rows for file with ID ${fileId}`);
    }
  }

  // Jobs API
  static async createJob(
    request: CreateJobRequest
  ): Promise<CreateJobResponse> {
    try {
      const response = await apiClient.post<CreateJobResponse>(
        "/v1/dap/jobs/start",
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error creating job:", error);
      throw new Error("Failed to create job");
    }
  }

  static async getJobs(
    status?: JobStatus,
    dataFileId?: number,
    skip: number = 0,
    limit: number = 100,
    includeStages: boolean = true,
    projectId?: number
  ): Promise<ApiResponse<Job>> {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (dataFileId) params.append('data_file_id', dataFileId.toString());
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      params.append('include_stages', includeStages.toString());
      const pid = projectId ?? ApiService.getCurrentProjectId();
      if (pid !== undefined) params.append('project_id', String(pid));

      // API returns Job[] directly, not wrapped in ApiResponse
      const response = await apiClient.get<Job[]>(
        `/v1/dap/jobs?${params.toString()}`
      );

      // Wrap the response in ApiResponse format for consistency
      return {
        data: response.data,
        total: response.data.length,
      };
    } catch (error) {
      console.error("Error fetching jobs:", error);
      throw new Error("Failed to fetch jobs");
    }
  }

  static async getJobById(id: number, includeStages: boolean = true): Promise<Job> {
    try {
      const response = await apiClient.get<Job>(
        `/v1/dap/jobs/${id}?include_stages=${includeStages}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching job ${id}:`, error);
      throw new Error(`Failed to fetch job with ID ${id}`);
    }
  }

  static async updateJobStatus(
    id: number,
    status: JobStatus,
    endedAt?: string
  ): Promise<Job> {
    try {
      const updateData: any = { status };
      if (endedAt) updateData.ended_at = endedAt;

      const response = await apiClient.patch<Job>(
        `/v1/dap/jobs/${id}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating job ${id}:`, error);
      throw new Error(`Failed to update job with ID ${id}`);
    }
  }

  // Get validation details for a specific job
  static async getValidationDetails(jobId: number): Promise<ValidationDetails> {
    try {
      const response = await apiClient.get<ValidationDetails>(
        `/v1/dap/jobs/${jobId}/validation`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error getting validation details for job ${jobId}:`,
        error
      );
      throw new Error(`Failed to get validation details for job ${jobId}`);
    }
  }

  // Get correction recommendation details for a specific job
  static async getCorrectionRecommendationDetails(jobId: number): Promise<any> {
    try {
      const response = await apiClient.get<any>(
        `/v1/dap/jobs/${jobId}/correction-recommendation`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error getting correction recommendation details for job ${jobId}:`,
        error
      );
      throw new Error(
        `Failed to get correction recommendation details for job ${jobId}`
      );
    }
  }

  static async approveCorrectionRecommendations(
    jobId: number,
    approvalData: {
      actions: Record<string, "accepted" | "rejected">;
      timestamp: string;
      approved_by?: string;
    }
  ): Promise<{
    job_id: number;
    status: string;
    message: string;
    approved_count: number;
    rejected_count: number;
    timestamp: string;
  }> {
    try {
      const response = await apiClient.post(
        `/v1/dap/jobs/${jobId}/correction-recommendation/approve`,
        approvalData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error approving correction recommendations for job ${jobId}:`,
        error
      );
      throw new Error(
        `Failed to approve correction recommendations for job ${jobId}`
      );
    }
  }

  static async rejectCorrectionRecommendations(
    jobId: number,
    rejectionData: {
      actions: Record<string, 'accepted' | 'rejected'>;
      timestamp: string;
      approved_by?: string;
    }
  ): Promise<{
    job_id: number;
    status: string;
    message: string;
    approved_count: number;
    rejected_count: number;
    timestamp: string;
  }> {
    try {
      const response = await apiClient.post(
        `/v1/dap/jobs/${jobId}/correction-recommendation/reject`,
        rejectionData
      );
      return response.data;
    } catch (error) {
      console.error(`Error rejecting correction recommendations for job ${jobId}:`, error);
      throw new Error(`Failed to reject correction recommendations for job ${jobId}`);
    }
  }
  
  // Profile Report API
  static async getProfileReports(
    skip: number = 0,
    limit: number = 100,
    jobId?: number,
    status?: string
  ): Promise<ProfileReportListResponse> {
    try {
      const params = new URLSearchParams();
      params.append("skip", skip.toString());
      params.append("limit", limit.toString());
      if (jobId) params.append("job_id", jobId.toString());
      if (status) params.append("status", status);

      const response = await apiClient.get<ProfileReportListResponse>(
        `/v1/dap/profile?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching profile reports:", error);
      throw new Error("Failed to fetch profile reports");
    }
  }

  static async getProfileReportById(id: number): Promise<ProfileReport> {
    try {
      const response = await apiClient.get<ProfileReport>(
        `/v1/dap/profile/${id}`
      );

      // Parse the report JSON string if it exists and is a string
      if (response.data.report && typeof response.data.report === "string") {
        try {
          response.data.report = JSON.parse(response.data.report);
        } catch (parseError) {
          console.warn("Failed to parse report JSON:", parseError);
          // Keep the original string if parsing fails
        }
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching profile report ${id}:`, error);
      throw new Error(`Failed to fetch profile report with ID ${id}`);
    }
  }

  static async getProfileReportsByJobId(
    jobId: number
  ): Promise<ProfileReportSummary[]> {
    try {
      const response = await apiClient.get<ProfileReportSummary[]>(
        `/v1/dap/profile/job/${jobId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching profile reports for job ${jobId}:`, error);
      throw new Error(`Failed to fetch profile reports for job ${jobId}`);
    }
  }

  // Data Persistence API
  static async checkDataPersistence(dataFileId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/v1/dap/data-persistence/check/${dataFileId}`, {
        timeout: 15000 // 15 seconds to account for the 10-second wait
      });
      return response.data;
    } catch (error) {
      console.error("Data persistence check failed:", error);
      throw error;
    }
  }

  static async getDataPersistenceLogs(dataFileId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/v1/dap/data-persistence/logs/${dataFileId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to get data persistence logs:", error);
      throw new Error("Failed to get data persistence logs");
    }
  }

  static async createDataPersistenceLog(logData: any): Promise<any> {
    try {
      const response = await apiClient.post(`/v1/dap/data-persistence/logs`, logData);
      return response.data;
    } catch (error) {
      console.error("Failed to create data persistence log:", error);
      throw new Error("Failed to create data persistence log");
    }
  }

  // Preflight API
  static async runPreflightCheck(fileId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/v1/dap/jobs/preflight/${fileId}`);
      return response.data;
    } catch (error) {
      console.error("Preflight check failed:", error);
      throw new Error("Failed to run preflight check");
    }
  }

  // Inference API
  static async runMetadataInference(jobId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/v1/dap/jobs/inference/${jobId}`);
      return response.data;
    } catch (error) {
      console.error("Metadata inference failed:", error);
      throw new Error("Failed to run metadata inference");
    }
  }

  static async viewMetadataFile(metadataFileId: number): Promise<any> {
    try {
      const response = await apiClient.get(
        `/v1/dap/jobs/inference/metadata/${metadataFileId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to view metadata file:", error);
      throw new Error("Failed to view metadata file content");
    }
  }

  // Metadata Validation API
  static async validateMetadataContent(metadataContent: any[]): Promise<any> {
    try {
      const response = await apiClient.post(
        "/v1/dap/files/metadata/validate-content",
        { metadata_content: metadataContent }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to validate metadata content:", error);
      throw new Error("Failed to validate metadata content");
    }
  }

  // Health Check
  static async healthCheck(): Promise<{ status: string; service: string }> {
    try {
      const response = await apiClient.get("/health");
      return response.data;
    } catch (error) {
      console.error("Health check failed:", error);
      throw new Error("API health check failed");
    }
  }

  //Edit Meta TableData
  static async editMetaDataTable(payload: any) {
    console.log("payload:", payload);
    // Transform payload to match backend schema
    const transformedPayload = {
      metadata_file_id: payload.file_id,
      full_metadata: payload.metadata_content
    };
    console.log("transformed payload:", transformedPayload);
    const response = await apiClient.put(
      "/v1/dap/jobs/inference/metadata/edit",
      transformedPayload
    );
    return response.data;
  }

  // Structural Relationships API
  static async saveStructuralRelationships(
    metadataFileId: number,
    relationships: Array<{
      source_column: string;
      target_column: string;
      relationship_factor: string;
    }>,
    user: string = "system"
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `/v1/dap/structural-relationships/metadata/${metadataFileId}/upsert`,
        {
          relationships,
          user
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to save structural relationships:", error);
      throw new Error("Failed to save structural relationships");
    }
  }

  static async getStructuralRelationships(metadataFileId: number): Promise<any> {
    try {
      const response = await apiClient.get(
        `/v1/dap/structural-relationships/metadata/${metadataFileId}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No relationships found
      }
      console.error("Failed to get structural relationships:", error);
      throw new Error("Failed to get structural relationships");
    }
  }

  static async deleteStructuralRelationships(metadataFileId: number): Promise<any> {
    try {
      const response = await apiClient.delete(
        `/v1/dap/structural-relationships/metadata/${metadataFileId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to delete structural relationships:", error);
      throw new Error("Failed to delete structural relationships");
    }
  }

  // Required Description Columns API methods
  static async saveRequiredDescriptionColumns(metadataFileId: number, columnRequirements: any): Promise<any> {
    try {
      const payload = {
        column_requirements: columnRequirements,
        created_by: "user"
      };
      
      const response = await apiClient.post(
        `/v1/dap/required-description-columns/metadata-file/${metadataFileId}/upsert`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Failed to save required description columns:", error);
      throw new Error("Failed to save required description columns");
    }
  }

  static async getRequiredDescriptionColumns(metadataFileId: number): Promise<any> {
    try {
      const response = await apiClient.get(
        `/v1/dap/required-description-columns/metadata-file/${metadataFileId}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // No required description columns found, return null
        return null;
      }
      console.error("Failed to get required description columns:", error);
      throw new Error("Failed to get required description columns");
    }
  }

  static async deleteRequiredDescriptionColumns(metadataFileId: number): Promise<any> {
    try {
      const response = await apiClient.delete(
        `/v1/dap/required-description-columns/metadata-file/${metadataFileId}`
      );
      return response.data;
    } catch (error) {
      console.error("Failed to delete required description columns:", error);
      throw new Error("Failed to delete required description columns");
    }
  }

  // Upload and view metadata file
  static async uploadAndViewMetadataFile(file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        '/v1/dap/jobs/inference/metadata/view',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000, // 30 seconds for file upload
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to upload and view metadata file:', error);
      throw new Error('Failed to upload and view metadata file');
    }
  }

  // Dummy API method for inferring relationships
  static async inferRelationships(payload: any): Promise<any> {
    try {
      const response = await apiClient.post(
        '/v1/dap/relationships/infer',
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Failed to infer relationships:', error);
      throw new Error('Failed to infer relationships');
    }
  }

  // Metadata Settings API
  static async saveMetadataSettings(settingsData: {
    metadataId: string;
    attributes: Record<string, { min: number; max: number }>;
  }): Promise<{
    settings_saved: any;
    message: string;
    id: number;
  }> {
    try {
      const response = await apiClient.post(
        '/v1/dap/metadata-settings/save',
        settingsData
      );
      return response.data;
    } catch (error) {
      console.error('Failed to save metadata settings:', error);
      throw new Error('Failed to save metadata settings');
    }
  }

  static async getMetadataSettings(metadataFileId: number): Promise<{
    id: number;
    metadata_file_id: number;
    settings_data: Record<string, any>;
    created_at: string;
    updated_at?: string;
    created_by: string;
    updated_by?: string;
  }> {
    try {
      const response = await apiClient.get(
        `/v1/dap/metadata-settings/${metadataFileId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get metadata settings for file ${metadataFileId}:`, error);
      throw new Error(`Failed to get metadata settings for file ${metadataFileId}`);
    }
  }

  static async getMetadataSettingsAttributes(metadataFileId: number): Promise<Record<string, { min: number; max: number }>> {
    try {
      const response = await apiClient.get(
        `/v1/dap/metadata-settings/${metadataFileId}/attributes`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get metadata settings attributes for file ${metadataFileId}:`, error);
      throw new Error(`Failed to get metadata settings attributes for file ${metadataFileId}`);
    }
  }

  // Rules API - Get profiling rules JSON by metadata file ID
  static async getProfilingRulesByMetadataId(metadataFileId: number): Promise<any> {
    try {
      const response = await apiClient.get(
        `/v1/dap/rules/metadata/${metadataFileId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to get profiling rules for metadata file ${metadataFileId}:`, error);
      throw new Error(`Failed to get profiling rules for metadata file ${metadataFileId}`);
    }
  }

  // Projects API
  static async getProjects(
    skip: number = 0,
    limit: number = 100,
    search?: string,
    tenant?: string
  ): Promise<ApiResponse<Project>> {
    try {
      const params = new URLSearchParams();
      params.append("skip", skip.toString());
      params.append("limit", limit.toString());
      if (search) params.append("search", search);
      if (tenant) params.append("tenant", tenant);

      const response = await apiClient.get<ApiResponse<Project>>(
        `/v1/dap/projects?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching projects:", error);
      throw new Error("Failed to fetch projects");
    }
  }

  static async getProjectById(id: number): Promise<Project> {
    try {
      const response = await apiClient.get<Project>(`/v1/dap/projects/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching project ${id}:`, error);
      throw new Error(`Failed to fetch project with ID ${id}`);
    }
  }

  static async createProject(projectData: ProjectCreate): Promise<Project> {
    try {
      const response = await apiClient.post<Project>("/v1/dap/projects", projectData);
      return response.data;
    } catch (error) {
      console.error("Error creating project:", error);
      throw new Error("Failed to create project");
    }
  }

  static async updateProject(id: number, projectData: ProjectUpdate): Promise<Project> {
    try {
      const response = await apiClient.put<Project>(`/v1/dap/projects/${id}`, projectData);
      return response.data;
    } catch (error) {
      console.error(`Error updating project ${id}:`, error);
      throw new Error(`Failed to update project with ID ${id}`);
    }
  }

  static async deleteProject(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(`/v1/dap/projects/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting project ${id}:`, error);
      throw new Error(`Failed to delete project with ID ${id}`);
    }
  }

  // Dynamic Tables API
  static async getDynamicTableData(
    tableName: string,
    page: number = 1,
    pageSize: number = 100
  ): Promise<any> {
    try {
      const response = await apiClient.get(
        `/v1/dap/dynamic-tables/data/${tableName}?page=${page}&page_size=${pageSize}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching data from dynamic table '${tableName}':`, error);
      throw new Error(`Failed to fetch data from dynamic table '${tableName}'`);
    }
  }

  static async getDynamicTableSchema(tableName: string): Promise<any> {
    try {
      const response = await apiClient.get(
        `/v1/dap/dynamic-tables/schema/${tableName}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching schema for dynamic table '${tableName}':`, error);
      throw new Error(`Failed to fetch schema for dynamic table '${tableName}'`);
    }
  }

  static async checkDynamicTableExists(tableName: string): Promise<boolean> {
    try {
      const response = await apiClient.get(
        `/v1/dap/dynamic-tables/exists/${tableName}`
      );
      return response.data.exists;
    } catch (error) {
      console.error(`Error checking if dynamic table '${tableName}' exists:`, error);
      return false;
    }
  }

  static async createDynamicTable(tableName: string, columns: Record<string, string>): Promise<any> {
    try {
      const response = await apiClient.post('/v1/dap/dynamic-tables/create', {
        table_name: tableName,
        columns: columns
      });
      return response.data;
    } catch (error) {
      console.error(`Error creating dynamic table '${tableName}':`, error);
      throw new Error(`Failed to create dynamic table '${tableName}'`);
    }
  }

  // Load CSV data into database
  static async loadCsvData(dataFileId: number, tableName?: string): Promise<any> {
    try {
      const payload: any = {
        data_file_id: dataFileId
      };
      
      // Add table_name to payload if provided
      if (tableName) {
        payload.table_name = tableName;
      }
      
      const response = await apiClient.post('/v1/dap/data-persistence/load-csv-data', payload);
      return response.data;
    } catch (error) {
      console.error(`Error loading CSV data for data file ID ${dataFileId}:`, error);
      throw new Error(`Failed to load CSV data for data file ID ${dataFileId}`);
    }
  }

  // Check if table exists for a data file
  static async checkTableExistsForDataFile(dataFileName: string): Promise<boolean> {
    try {
      // Remove file extension to get table name
      const tableName = dataFileName.replace(/\.[^/.]+$/, "");
      return await this.checkDynamicTableExists(tableName);
    } catch (error) {
      console.error(`Error checking if table exists for file ${dataFileName}:`, error);
      return false;
    }
  }

  static async createDataMetadataMapping(dataFileId: number, metadataFileId: number, createdBy: string = "system"): Promise<any> {
    try {
      const response = await apiClient.post('/v1/dap/data-metadata-mapper/', {
        data_file_id: dataFileId,
        metadata_file_id: metadataFileId,
        created_by: createdBy
      });
      return response.data;
    } catch (error) {
      console.error(`Error creating data-metadata mapping:`, error);
      throw new Error(`Failed to create data-metadata mapping`);
    }
  }

  // Get metadata file ID by data file ID using data_metadata_mapper
  static async getMetadataFileIdByDataFileId(dataFileId: number): Promise<number | null> {
    try {
      const response = await apiClient.get(`/v1/dap/data-metadata-mapper/data-file/${dataFileId}`);
      if (response.data && response.data.length > 0) {
        // Return the metadata_file_id from the first mapping found
        return response.data[0].metadata_file_id;
      }
      return null;
    } catch (error) {
      console.error(`Error getting metadata file ID for data file ${dataFileId}:`, error);
      return null;
    }
  }
}

// DQR Report API methods
export const getDQRColumns = async (tableName?: string): Promise<{ columns: string[], total_columns: number }> => {
  try {
    const params = new URLSearchParams();
    if (tableName) params.append('table_name', tableName);
    
    const response = await apiClient.get(`/v1/dap/dqr-output/columns${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching DQR columns:', error);
    throw new Error('Failed to fetch DQR columns');
  }
};

export const getDQRRows = async (
  page: number = 1,
  pageSize: number = 10,
  filters: Record<string, string> = {},
  tableName?: string
): Promise<{
  rows: any[],
  metadata: {
    page: number,
    page_size: number,
    total_rows: number,
    total_pages: number,
    columns: string[]
  }
}> => {
  try {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    if (tableName) params.append('table_name', tableName);
    
    // Add filters to params
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== 'page' && key !== 'page_size' && key !== 'table_name') {
        console.log(`Adding filter: ${key}=${value}, type: ${typeof value}`);
        // Ensure we never send undefined values
        params.append(key, value !== undefined ? value : 'true');
      }
    });
    
    const requestUrl = `/v1/dap/dqr-output/rows?${params.toString()}`;
    console.log('Making request to:', requestUrl);
    console.log('Filters:', JSON.stringify(filters));
    
    const response = await apiClient.get(requestUrl);
    return response.data;
  } catch (error) {
    console.error('Error fetching DQR rows:', error);
    console.error('Request URL that failed:', `/v1/dap/dqr-output/rows?page=${page}&page_size=${pageSize}&${Object.entries(filters).map(([k,v]) => `${k}=${v}`).join('&')}`);
    throw new Error('Failed to fetch DQR rows');
  }
};

export const getDQRStats = async (tableName?: string): Promise<{
  total_rows: number,
  total_columns: number,
  column_stats: Record<string, { true_count: number, false_count: number, true_percentage: number }>
}> => {
  try {
    const params = new URLSearchParams();
    if (tableName) params.append('table_name', tableName);
    
    const response = await apiClient.get(`/v1/dap/dqr-output/stats${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching DQR stats:', error);
    throw new Error('Failed to fetch DQR stats');
  }
};

export default ApiService;
